// Press Kit — boilerplate, fast facts, logo assets, downloadable one-pager for media.
import { useState } from "react";
import { toast } from "sonner";
import { Download, Copy, Mail, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PageHeader, Kicker } from "@/components/Section";
import { useOrgSettings } from "@/lib/org";

const BOILERPLATE = (org) =>
  `Big Little Village is a ${org.nonprofit_designation.toLowerCase().includes("501") ? "501(c)(3) nonprofit" : "nonprofit"} creative movement giving emerging creatives the real experience, collaboration, and portfolios that open doors. Through mission-based creative cohorts, students and early-career creatives work on real campaigns with real mentors — and own the portfolio work that comes out of them. Founding cohorts launch in ${org.founding_location}.`;

const wrapText = (ctx, text, x, y, maxWidth, lineHeight) => {
  const words = text.split(" ");
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, y);
      line = w;
      y += lineHeight;
    } else {
      line = test;
    }
  }
  ctx.fillText(line, x, y);
  return y + lineHeight;
};

const drawOnePager = async (org) => {
  await document.fonts.ready;
  const logo = await new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = "/branding-logo.png";
  });
  const W = 1080, H = 1400;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "#C9A227";
  ctx.lineWidth = 3;
  ctx.strokeRect(40, 40, W - 80, H - 80);

  ctx.drawImage(logo, W / 2 - 130, 80, 260, 260);
  ctx.textAlign = "center";
  ctx.fillStyle = "#0A0A0B";
  ctx.font = "64px Anton, Impact, sans-serif";
  ctx.fillText("BIG LITTLE VILLAGE", W / 2, 430);
  ctx.fillStyle = "#C9A227";
  ctx.font = "600 24px Cinzel, serif";
  ctx.fillText("P R E S S   O N E - P A G E R", W / 2, 475);

  ctx.textAlign = "left";
  ctx.fillStyle = "#0A0A0B";
  ctx.font = "600 22px Cinzel, serif";
  ctx.fillText("ABOUT", 90, 560);
  ctx.font = "22px Arial, sans-serif";
  ctx.fillStyle = "#333";
  let y = wrapText(ctx, BOILERPLATE(org), 90, 600, W - 180, 34);

  ctx.fillStyle = "#0A0A0B";
  ctx.font = "600 22px Cinzel, serif";
  ctx.fillText("FAST FACTS", 90, y + 40);
  ctx.font = "22px Arial, sans-serif";
  ctx.fillStyle = "#333";
  y += 80;
  const facts = [
    `Organization: ${org.org_name} — ${org.org_tagline}`,
    `Status: ${org.nonprofit_designation}${org.ein ? ` · EIN: ${org.ein}` : ""}`,
    `Founding Cohorts: ${org.founding_location}`,
    "Model: Mission-based creative cohorts across photography, film, design, styling, beauty & more",
    "Founding Cohort applications: September 5 – October 31, 2026",
  ];
  for (const f of facts) {
    y = wrapText(ctx, `•  ${f}`, 90, y, W - 180, 34) + 6;
  }

  ctx.fillStyle = "#0A0A0B";
  ctx.font = "600 22px Cinzel, serif";
  ctx.fillText("MEDIA CONTACT", 90, y + 40);
  ctx.font = "22px Arial, sans-serif";
  ctx.fillStyle = "#333";
  ctx.fillText(org.email_general, 90, y + 80);
  ctx.fillText("downintheville.org", 90, y + 114);

  ctx.textAlign = "center";
  ctx.fillStyle = "#C9A227";
  ctx.font = "600 20px Cinzel, serif";
  ctx.fillText("W E   A R E   L I T T L E .   B U T   O U R   I D E A S   A R E   B I G .", W / 2, H - 80);
  return canvas;
};

export default function Press() {
  const org = useOrgSettings();
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const copyBoilerplate = async () => {
    try {
      await navigator.clipboard.writeText(BOILERPLATE(org));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — select the text manually.");
    }
  };

  const downloadOnePager = async () => {
    setBusy(true);
    try {
      const canvas = await drawOnePager(org);
      const a = document.createElement("a");
      a.download = "big-little-village-press-onepager.png";
      a.href = canvas.toDataURL("image/png");
      a.click();
    } catch {
      toast.error("Couldn't build the one-pager. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="press-page">
      <PageHeader
        kicker="For Media"
        titleLines={["Press", "kit."]}
        subtitle="Everything you need to cover the Village — boilerplate, facts, logos, and a one-pager."
        dark
      />

      <section className="grain-dark border-b border-gold/20 bg-onyx py-16 sm:py-20">
        <div className="mx-auto max-w-[860px] px-4 sm:px-6">
          <Reveal>
            <Kicker>Boilerplate</Kicker>
            <div className="mt-6 border border-gold/25 bg-charcoal/70 p-6 sm:p-8">
              <p className="font-body text-base leading-relaxed text-silver/90" data-testid="press-boilerplate">{BOILERPLATE(org)}</p>
              <button onClick={copyBoilerplate} data-testid="copy-boilerplate-btn"
                className="mt-5 inline-flex items-center gap-2 border border-silver/40 px-5 py-2.5 font-head text-xs font-extrabold uppercase tracking-[0.2em] text-silver transition-colors hover:border-gold hover:text-goldlight">
                {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy Boilerplate"}
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="grain-dark border-b border-gold/20 bg-charcoal py-16 sm:py-20">
        <div className="mx-auto max-w-[860px] px-4 sm:px-6">
          <Reveal>
            <Kicker>Fast facts</Kicker>
            <ul className="mt-6 space-y-3" data-testid="press-fast-facts">
              {[
                ["Organization", `${org.org_name} — ${org.org_tagline}`],
                ["Status", `${org.nonprofit_designation}${org.ein ? ` · EIN: ${org.ein}` : ""}`],
                ["Founding Cohorts", org.founding_location],
                ["Model", "Mission-based creative cohorts across photography, film, design, styling, beauty & more"],
                ["Applications", "Founding Cohort window: September 5 – October 31, 2026"],
              ].map(([k, v]) => (
                <li key={k} className="flex flex-col gap-1 border border-silver/15 bg-onyx/70 p-4 sm:flex-row sm:items-baseline sm:gap-4">
                  <span className="w-44 shrink-0 font-mono text-[9px] uppercase tracking-[0.3em] text-gold">{k}</span>
                  <span className="font-body text-sm text-silver/85">{v}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="grain-dark border-b border-gold/20 bg-onyx py-16 sm:py-20">
        <div className="mx-auto max-w-[860px] px-4 sm:px-6">
          <Reveal>
            <Kicker>Assets & downloads</Kicker>
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <div className="border border-silver/20 bg-charcoal/70 p-6 text-center">
                <img src="/branding-logo.png" alt="Big Little Village logo" className="mx-auto h-36 w-36 object-contain" loading="lazy" />
                <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.3em] text-silver/60">Primary crest logo · PNG</p>
                <a href="/branding-logo.png" download="big-little-village-logo.png" data-testid="download-logo-btn"
                  className="btn-sweep mt-4 inline-flex items-center gap-2 border border-gold bg-gradient-to-b from-goldlight to-gold px-5 py-2.5 font-head text-xs font-extrabold uppercase tracking-[0.2em] text-onyx transition-transform duration-200 hover:-translate-y-0.5">
                  <Download size={13} /> Download Logo
                </a>
              </div>
              <div className="flex flex-col items-center justify-center border border-silver/20 bg-charcoal/70 p-6 text-center">
                <p className="font-cinzel text-[10px] uppercase tracking-[0.35em] text-gold">Press one-pager</p>
                <p className="mt-2 max-w-xs font-body text-sm text-silver/70">Mission, credentials, and contact on a single shareable page.</p>
                <button onClick={downloadOnePager} disabled={busy} data-testid="download-onepager-btn"
                  className="btn-sweep mt-4 inline-flex items-center gap-2 border border-gold bg-gradient-to-b from-goldlight to-gold px-5 py-2.5 font-head text-xs font-extrabold uppercase tracking-[0.2em] text-onyx transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
                  <Download size={13} /> {busy ? "Building..." : "Download One-Pager"}
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="grain-dark bg-charcoal py-16 text-center sm:py-20">
        <div className="mx-auto max-w-[720px] px-4 sm:px-6">
          <Reveal>
            <Kicker>Media contact</Kicker>
            <p className="mt-5 font-body text-base text-silver/85">For interviews, quotes, and press inquiries:</p>
            <a href={`mailto:${org.email_general}`} data-testid="press-contact-email"
              className="mt-2 inline-flex items-center gap-2 font-mono text-sm font-bold uppercase tracking-[0.1em] text-goldlight underline decoration-gold/40 underline-offset-4 transition-colors hover:text-gold">
              <Mail size={14} /> {org.email_general}
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
